import Header from '../components/Header';
import Footer from '../components/Footer';
import { Cookie, Settings, Shield } from 'lucide-react';

const CookiePolicy = () => {
  return (
    <div className="min-h-screen bg-regal-dark">
      <Header />
      <main className="py-12 md:py-20">
        <div className="container mx-auto px-4">
          <article className="max-w-4xl mx-auto">
            <div className="mb-8">
              <h1 className="text-4xl md:text-5xl font-bold mb-4 text-white">
                Cookie Policy
              </h1>
              <p className="text-gray-400">Last updated: December 2, 2025</p>
            </div>
            
            <div className="prose prose-invert max-w-none">
              <div className="bg-regal-gray rounded-xl p-8 mb-8 border border-gray-800">
                <p className="text-xl text-gray-300 leading-relaxed">
                  This Cookie Policy explains how Regal Play uses cookies and similar tracking technologies on our website regal-play.com. It describes what cookies are, how we use them, and your choices regarding cookie management.
                </p>
              </div>

              <div className="space-y-8 text-gray-300 leading-relaxed">
                <section>
                  <div className="flex items-center gap-3 mb-4">
                    <Cookie className="w-8 h-8 text-regal-red" />
                    <h2 className="text-3xl font-bold text-white">What Are Cookies?</h2>
                  </div>
                  <p>
                    Cookies are small text files that are placed on your device when you visit a website. They are widely used to make websites work more efficiently and to provide information to website owners about how users interact with their sites.
                  </p>
                  <p>
                    Cookies can be "persistent" or "session" cookies. Persistent cookies remain on your device after you close your browser until they expire or you delete them. Session cookies are temporary and are deleted when you close your browser.
                  </p>
                  <p>
                    Cookies serve various purposes, including remembering your preferences, analyzing website traffic, enabling certain website features, and tracking user interactions for marketing and analytics purposes.
                  </p>
                </section>

                <section>
                  <h2 className="text-3xl font-bold text-white mb-4">How We Use Cookies</h2>
                  <p>
                    Regal Play uses cookies for several essential purposes to enhance your browsing experience and enable website functionality. We use cookies to remember your preferences, analyze how visitors use our website, and track affiliate link clicks for commission purposes.
                  </p>
                  <p>
                    Essential cookies are necessary for the website to function properly. These cookies enable core functionality such as security, network management, and accessibility. Without these cookies, services you have requested cannot be provided.
                  </p>
                  <p>
                    Analytics cookies help us understand how visitors interact with our website by collecting and reporting information anonymously. This data helps us improve website design, content, and user experience.
                  </p>
                  <p>
                    Affiliate tracking cookies allow us to track when users click on casino operator links, enabling us to receive commissions for referrals. These cookies are essential for our business model and help us maintain and operate our free comparison service.
                  </p>
                </section>

                <section>
                  <div className="flex items-center gap-3 mb-4">
                    <Settings className="w-8 h-8 text-regal-red" />
                    <h2 className="text-3xl font-bold text-white">Types of Cookies We Use</h2>
                  </div>
                  <p>
                    <strong className="text-white">Essential Cookies:</strong> These cookies are necessary for the website to function and cannot be switched off in our systems. They are usually set in response to actions made by you, such as setting privacy preferences, logging in, or filling in forms.
                  </p>
                  <p>
                    <strong className="text-white">Performance Cookies:</strong> These cookies allow us to count visits and traffic sources so we can measure and improve the performance of our website. They help us understand which pages are most popular and how visitors move around the site.
                  </p>
                  <p>
                    <strong className="text-white">Functionality Cookies:</strong> These cookies enable the website to provide enhanced functionality and personalization. They may be set by us or by third-party providers whose services we have added to our pages.
                  </p>
                  <p>
                    <strong className="text-white">Targeting Cookies:</strong> These cookies may be set through our site by our advertising partners to build a profile of your interests and show you relevant content on other sites. They do not store directly personal information but work by uniquely identifying your browser and device.
                  </p>
                </section>

                <section>
                  <h2 className="text-3xl font-bold text-white mb-4">Third-Party Cookies</h2>
                  <p>
                    In addition to our own cookies, we may also use various third-party cookies to report usage statistics of the website, deliver advertisements, and provide enhanced functionality. These third-party services may set their own cookies on your device.
                  </p>
                  <p>
                    Our affiliate partners may place cookies on your device when you click on casino operator links. These cookies help track referrals and enable us to receive commissions. Each affiliate partner has its own cookie policy, and we encourage you to review their policies for more information.
                  </p>
                  <p>
                    Analytics providers such as Google Analytics use cookies to help us analyze how visitors use our website. The information generated by these cookies about your use of the website is transmitted to and stored by analytics providers.
                  </p>
                  <p>
                    We do not control third-party cookies, and their use is governed by the privacy policies of the respective third parties. We recommend reviewing these policies to understand how third parties use cookies and what options you have for managing them.
                  </p>
                </section>

                <section>
                  <div className="flex items-center gap-3 mb-4">
                    <Shield className="w-8 h-8 text-regal-red" />
                    <h2 className="text-3xl font-bold text-white">Managing Your Cookie Preferences</h2>
                  </div>
                  <p>
                    Most web browsers automatically accept cookies, but you can modify your browser settings to decline cookies if you prefer. However, disabling cookies may prevent you from taking full advantage of our website's features and functionality.
                  </p>
                  <p>
                    You can manage cookies through your browser settings. Most browsers allow you to refuse or accept cookies, delete existing cookies, or receive notifications before cookies are set. The process for managing cookies varies by browser, so consult your browser's help documentation.
                  </p>
                  <p>
                    For more information about managing cookies in specific browsers, visit:
                  </p>
                  <ul className="list-disc list-inside ml-4 space-y-2 mt-4">
                    <li>Google Chrome: Settings → Privacy and security → Cookies and other site data</li>
                    <li>Mozilla Firefox: Options → Privacy & Security → Cookies and Site Data</li>
                    <li>Microsoft Edge: Settings → Privacy, search, and services → Cookies and site permissions</li>
                    <li>Safari: Preferences → Privacy → Manage Website Data</li>
                  </ul>
                  <p>
                    You can also opt out of certain third-party advertising cookies by visiting the Network Advertising Initiative opt-out page or the Digital Advertising Alliance opt-out page. However, these opt-out mechanisms may not work for all cookies or all devices.
                  </p>
                </section>

                <section>
                  <h2 className="text-3xl font-bold text-white mb-4">Cookie Consent</h2>
                  <p>
                    When you first visit our website, we may display a cookie consent banner that asks for your permission to use certain types of cookies. You can choose to accept all cookies, reject non-essential cookies, or customize your cookie preferences.
                  </p>
                  <p>
                    Your cookie preferences are stored in a cookie on your device. If you delete this cookie, you will need to set your preferences again on your next visit. You can change your cookie preferences at any time through our cookie settings or your browser settings.
                  </p>
                  <p>
                    Please note that some cookies are essential for the website to function and cannot be disabled. Essential cookies do not require your consent, but we inform you about them so you understand their purpose.
                  </p>
                </section>

                <section>
                  <h2 className="text-3xl font-bold text-white mb-4">Do Not Track Signals</h2>
                  <p>
                    Some browsers incorporate "Do Not Track" (DNT) features that send signals to websites you visit indicating your preference not to be tracked. Currently, there is no standard for how websites should respond to DNT signals.
                  </p>
                  <p>
                    We do not currently respond to DNT browser signals or mechanisms. However, you can control cookies and tracking through the methods described in this Cookie Policy, including browser settings and cookie management tools.
                  </p>
                </section>

                <section>
                  <h2 className="text-3xl font-bold text-white mb-4">Updates to This Cookie Policy</h2>
                  <p>
                    We may update this Cookie Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. We will notify you of any material changes by posting the new Cookie Policy on this page and updating the "Last updated" date.
                  </p>
                  <p>
                    We encourage you to review this Cookie Policy periodically to stay informed about how we use cookies. Your continued use of our website after any changes to this Cookie Policy constitutes your acceptance of the updated terms.
                  </p>
                </section>

                <section>
                  <h2 className="text-3xl font-bold text-white mb-4">Contact Us</h2>
                  <p>
                    If you have questions about our use of cookies or this Cookie Policy, please contact us:
                  </p>
                  <div className="bg-regal-gray rounded-lg p-6 border border-gray-800 mt-4">
                    <p className="text-white font-semibold mb-2">Regal Play</p>
                    <p className="text-gray-300">Email: info@regal-play.com</p>
                    <p className="text-gray-300">Phone: +44-0544-403-303</p>
                    <p className="text-gray-300">Address: London, United Kingdom</p>
                  </div>
                </section>
              </div>
            </div>
          </article>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default CookiePolicy;

